import { useCallback, useEffect, useMemo, useState } from "react";
import { C } from "../lib/theme.js";
import { api } from "../lib/api.js";

// Editable table of property metadata (alias, units, ownership %).
// Source of truth is the `properties` DB table; ownership % is stored in the
// `settings.ownership` blob. "Save all" writes both in one click.
//
// `ownerPcts` / `setOwnerPcts` mirror the parent dashboard state so the UI
// reflects ownership changes immediately after save without a page reload.
export default function ManageProperties({ ownerPcts, setOwnerPcts, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [initialOwnerPcts, setInitialOwnerPcts] = useState({});
  const [localPcts, setLocalPcts] = useState(ownerPcts || {});
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [rowBusy, setRowBusy] = useState(null);

  const fetchRows = useCallback(async (includeArchived) => {
    setLoading(true);
    try {
      const list = await api.listProperties({ includeArchived });
      const sorted = (list || []).slice().sort((a, b) => (a.code || "").localeCompare(b.code || ""));
      setRows(sorted.map((r) => ({
        code: r.code,
        name: r.name || r.code,
        alias: r.alias || r.code,
        units: r.units || 0,
        default_pct: r.default_pct ?? null,
        archived_at: r.archived_at || null,
        _initial: {
          alias: r.alias || r.code,
          units: r.units || 0,
        },
      })));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRows(showArchived);
  }, [fetchRows, showArchived]);

  useEffect(() => {
    setInitialOwnerPcts({ ...(ownerPcts || {}) });
    setLocalPcts({ ...(ownerPcts || {}) });
    // Only want to seed localPcts on mount; parent hands us fresh identity per render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dirty = useMemo(() => {
    if (rows.some((r) =>
      !r.archived_at && (
        r.alias !== r._initial.alias ||
        Number(r.units) !== Number(r._initial.units)
      )
    )) return true;
    const keys = new Set([...Object.keys(localPcts), ...Object.keys(initialOwnerPcts)]);
    for (const k of keys) {
      if ((localPcts[k] || 0) !== (initialOwnerPcts[k] || 0)) return true;
    }
    return false;
  }, [rows, localPcts, initialOwnerPcts]);

  const activeRows = rows.filter((r) => !r.archived_at);
  const archivedRows = rows.filter((r) => r.archived_at);
  const configuredCount = activeRows.filter((r) => (localPcts[r.code] || 0) > 0).length;

  const update = (code, patch) =>
    setRows((prev) => prev.map((r) => (r.code === code ? { ...r, ...patch } : r)));

  const updatePct = (code, pctStr) => {
    const pct = parseFloat(pctStr);
    const v = Number.isFinite(pct) ? pct / 100 : 0;
    setLocalPcts((prev) => ({ ...prev, [code]: v }));
  };

  const save = async () => {
    setBusy(true);
    setStatus(null);
    try {
      // Only save edits for active rows; archived rows are read-only here.
      const items = activeRows.map((r) => ({
        code: r.code,
        name: r.name,
        alias: r.alias,
        units: Number(r.units) || 0,
        default_pct: r.default_pct ?? null,
      }));
      await api.saveProperties(items);
      setOwnerPcts(localPcts);
      await onSaved?.(localPcts);
      setInitialOwnerPcts({ ...localPcts });
      setRows((prev) => prev.map((r) => ({
        ...r,
        _initial: {
          alias: r.alias,
          units: r.units,
        },
      })));
      setStatus("Saved");
      setTimeout(() => setStatus(null), 2000);
    } catch (e) {
      setStatus("Error: " + e.message);
    }
    setBusy(false);
  };

  const handleArchive = async (row) => {
    const ok = window.confirm(
      `Archive "${row.alias}"? It will be hidden from the dashboard and future uploads will skip its sheet. You can restore it anytime from "Show archived".`,
    );
    if (!ok) return;
    setRowBusy(row.code);
    try {
      await api.archiveProperty(row.code);
      await fetchRows(showArchived);
      await onSaved?.(localPcts);
    } catch (e) {
      setStatus("Error: " + e.message);
    }
    setRowBusy(null);
  };

  const handleRestore = async (row) => {
    const ok = window.confirm(
      `Restore "${row.alias}"? Future uploads will ingest its sheets again.`,
    );
    if (!ok) return;
    setRowBusy(row.code);
    try {
      await api.restoreProperty(row.code);
      await fetchRows(showArchived);
      await onSaved?.(localPcts);
    } catch (e) {
      setStatus("Error: " + e.message);
    }
    setRowBusy(null);
  };

  const handlePurge = async (row) => {
    const ok = window.confirm(
      `Permanently delete "${row.alias}" and all its financial data? This cannot be undone.`,
    );
    if (!ok) return;
    setRowBusy(row.code);
    try {
      await api.purgeProperty(row.code);
      await fetchRows(showArchived);
      await onSaved?.(localPcts);
    } catch (e) {
      setStatus("Error: " + e.message);
    }
    setRowBusy(null);
  };

  if (loading && rows.length === 0) {
    return <div style={{ color: C.sub, fontSize: 13 }}>Loading properties…</div>;
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 12 }}>
        <div style={{ fontSize: 12, color: C.sub }}>
          {activeRows.length} active properties ·{" "}
          {archivedRows.length > 0 ? `${archivedRows.length} archived · ` : ""}
          {configuredCount} with ownership % configured
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: C.sub, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
            Show archived
          </label>
          {status && <span style={{ fontSize: 11, color: C.sub }}>{status}</span>}
          <button
            onClick={save}
            disabled={busy || !dirty}
            style={{
              background: dirty ? C.accent : C.borderLight,
              color: dirty ? "#fff" : C.sub,
              border: "none",
              borderRadius: 5,
              padding: "6px 14px",
              fontSize: 12,
              fontWeight: 600,
              cursor: busy || !dirty ? "not-allowed" : "pointer",
              opacity: busy ? 0.7 : 1,
            }}
          >
            {busy ? "Saving…" : "Save all"}
          </button>
        </div>
      </div>

      <div style={{ overflowX: "auto", border: `1px solid ${C.border}`, borderRadius: 6 }}>
        <div style={{ minWidth: 800 }}>
          <div style={{ ...headerRow }}>
            <span>Code</span>
            <span>Alias</span>
            <span style={{ textAlign: "right" }}>Units</span>
            <span style={{ textAlign: "right" }}>Ownership %</span>
            <span />
          </div>
          {rows.map((r) => {
            const pct = localPcts[r.code] || 0;
            const configured = pct > 0;
            const archived = !!r.archived_at;
            const rowStyle = {
              ...dataRow,
              opacity: archived ? 0.55 : 1,
              background: archived ? C.bg : "transparent",
            };
            return (
              <div key={r.code} style={rowStyle}>
                <span style={{ fontFamily: "'DM Mono',monospace", color: C.sub, fontSize: 12 }}>{r.code}</span>
                <input
                  value={r.alias}
                  onChange={(e) => update(r.code, { alias: e.target.value })}
                  disabled={archived}
                  style={{ ...inputStyle, background: archived ? C.borderLight : "#fff" }}
                />
                <input
                  type="number"
                  value={r.units}
                  min={0}
                  onChange={(e) => update(r.code, { units: e.target.value })}
                  disabled={archived}
                  style={{
                    ...inputStyle,
                    fontFamily: "'DM Mono',monospace",
                    textAlign: "right",
                    background: archived ? C.borderLight : "#fff",
                  }}
                />
                <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "flex-end" }}>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step={0.1}
                    value={(pct * 100).toFixed(1)}
                    onChange={(e) => updatePct(r.code, e.target.value)}
                    disabled={archived}
                    style={{
                      ...inputStyle,
                      width: 72,
                      fontFamily: "'DM Mono',monospace",
                      textAlign: "right",
                      borderColor: configured ? C.accent : C.border,
                      background: archived ? C.borderLight : configured ? C.accentLight : "#fff",
                    }}
                  />
                  <span style={{ fontSize: 11, color: C.dim }}>%</span>
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 6 }}>
                  {archived ? (
                    <>
                      <button
                        onClick={() => handleRestore(r)}
                        disabled={rowBusy === r.code}
                        style={{ ...actionButton, borderColor: C.accent, color: C.accent }}
                      >
                        {rowBusy === r.code ? "…" : "Restore"}
                      </button>
                      <button
                        onClick={() => handlePurge(r)}
                        disabled={rowBusy === r.code}
                        style={{ ...actionButton, borderColor: "#c44", color: "#c44" }}
                      >
                        {rowBusy === r.code ? "…" : "Delete"}
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => handleArchive(r)}
                      disabled={rowBusy === r.code}
                      style={{ ...actionButton, borderColor: C.border, color: C.sub }}
                    >
                      {rowBusy === r.code ? "…" : "Remove"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const headerRow = {
  display: "grid",
  gridTemplateColumns: "70px 1fr 90px 140px 150px",
  gap: 8,
  padding: "8px 12px",
  background: C.bg,
  borderBottom: `1px solid ${C.border}`,
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: C.dim,
};

const dataRow = {
  display: "grid",
  gridTemplateColumns: "70px 1fr 90px 140px 150px",
  gap: 8,
  padding: "8px 12px",
  borderBottom: `1px solid ${C.borderLight}`,
  alignItems: "center",
};

const inputStyle = {
  width: "100%",
  padding: "5px 8px",
  border: `1px solid ${C.border}`,
  borderRadius: 4,
  fontSize: 12,
  fontFamily: "'Inter',sans-serif",
  background: "#fff",
};

const actionButton = {
  padding: "4px 10px",
  fontSize: 11,
  fontWeight: 600,
  border: "1px solid",
  borderRadius: 4,
  background: "#fff",
  cursor: "pointer",
};
